import React, { useEffect, useRef, useState } from 'react';
import { useTranslation, Trans } from 'react-i18next';
import SettingsBox from '@medflex/components/stories/SettingsBox';
import FileInput from '@medflex/components/stories/FileUpload/FileInput';
import { showAlertToast } from '@medflex/components/stories/Toast';
import useUppy from '@medflex/hooks/useUppy';
import useUserSession from '@medflex/hooks/useUserSession';
import { gdtHelpers } from '@medflex/gdt';
import Logger from '@medflex/utils/Logger';
import { trackCustomEvent } from '@medflex/utils/Tracking';
import makeApiRequest from '../../../../api/makeApiRequest';
import useAddressBookContacts from '../../../hooks/useAddressBookContacts';
import useUserProfile from '../../../hooks/useUserProfile';
import FileUploadView from './FileUploadView';

const UploadFileSettingsBox = () => {
  const { session } = useUserSession();
  const { userProfile } = useUserProfile();
  const { t } = useTranslation('physician', { keyPrefix: 'PVSIntegrationModule.uploadSyncFile' });
  const { allPatients, load: loadAddressBookContacts } = useAddressBookContacts(true);
  const dropTarget = useRef();
  const [isSyncSucceeded, setIsSyncSucceeded] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [fileData, setFileData] = useState();
  const [matchCount, setMatchCount] = useState({ matched: 0, unmatched: 0 });

  const physicianUserId = userProfile.flags.isPhysician
    ? userProfile.id
    : userProfile.attributes.physicianUserId;

  const handleError = (_, e) => {
    showAlertToast(t('uploadError'), e.message, { toastId: 'bdtUploadError' });
  };

  const { files, reset, register } = useUppy({
    dropTarget,
    restrictions: {
      allowedFileTypes: ['application/octet-stream'],
    },
    onRestrictionFailed: handleError,
  });

  useEffect(() => {
    setIsSyncSucceeded(null);
    setFileData(files[0]);
  }, [files]);

  const handleSubmit = async () => {
    setIsLoading(true);

    const { matches, pvsPatients } = await gdtHelpers.syncPatients.multiSync(
      files[0].data.path,
      allPatients,
    );

    if (!matches.length) {
      setIsSyncSucceeded(true);
      setMatchCount({ matched: 0, unmatched: pvsPatients.length });
      setIsLoading(false);
      return;
    }

    const [request] = makeApiRequest(
      {
        url: `/physicians/${physicianUserId}/contacts`,
        method: 'patch',
        data: {
          data: matches.map(match => ({
            type: 'contacts',
            id: match.id,
            attributes: { pvsId: match.pvsId },
          })),
        },
      },
      session,
    );

    const { error } = await request;

    if (error) {
      setIsSyncSucceeded(false);
      setIsLoading(false);
      Logger.error('[UploadFileSettingsBox]: Error while multi syncing patients', error);
      return;
    }

    loadAddressBookContacts();

    const matched = matches.length;
    const unmatched = pvsPatients.length - matched;

    setIsSyncSucceeded(true);
    setMatchCount({ matched, unmatched });
    setIsLoading(false);

    trackCustomEvent({
      eventCategory: 'pvs_integration',
      eventAction: 'sync_patients_multi',
      eventName: 'sync_patient_multi_messenger',
    });
  };

  return (
    <div ref={dropTarget}>
      <FileInput {...register()} accept=".bdt,.gdt">
        {({ select }) => (
          <SettingsBox title={t('title')}>
            <p className="font-semibold mb-2">{t('subtitle')}</p>
            <p className="mb-4 whitespace-pre-line">
              <Trans t={t} i18nKey="description">
                <a
                  href="https://medflex.de/hilfe/pvs-integration/start/"
                  target="_blank"
                  className="hover:underline text-blue-500"
                  rel="noreferrer"
                >
                  Instructions for BDT-Export
                </a>
              </Trans>
            </p>
            <FileUploadView
              fileData={fileData}
              isLoading={isLoading}
              isSyncSucceeded={isSyncSucceeded}
              select={select}
              matchCount={matchCount}
              reset={reset}
              handleSubmit={handleSubmit}
            />
          </SettingsBox>
        )}
      </FileInput>
    </div>
  );
};

export default UploadFileSettingsBox;
